import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DocumentCheckIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';

export default function Navigation({ onCapacityTest }) {
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  const hasShownRef = useRef(false);
  const pageEnterMsRef = useRef(Date.now());
  const lastScrollYRef = useRef(0);
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  // Local testing helper: auto-show after 15s on every page load.
  const FORCE_TEST_POPUP_ON_LOCALHOST = false;
  const POPUP_SUPPRESS_KEY = 'smileconsent_trial_exit_popup_suppress_until';
  const POPUP_CONVERTED_KEY = 'smileconsent_trial_exit_popup_started';
  /** Legacy keys from previous “book a demo” exit popup — still respected so users are not re-nagged. */
  const LEGACY_DEMO_SUPPRESS_KEY = 'smileconsent_demo_popup_suppress_until';
  const LEGACY_DEMO_SUBMITTED_KEY = 'smileconsent_demo_popup_submitted';

  useEffect(() => {
    let mobileTimer = null;
    let desktopFallbackInterval = null;
    let hardFallbackTimer = null;
    pageEnterMsRef.current = Date.now();
    hasShownRef.current = false;
    lastScrollYRef.current = window.scrollY || 0;

    setShowTrialPopup(false);

    // Only show on public marketing pages (not auth/clinic/admin/internal).
    const path = location.pathname || '';
    const blockedPrefix = ['/clinic', '/admin', '/login', '/register', '/forgot-password', '/reset-password', '/setup', '/consent', '/ai-consent', '/verify-dob', '/sign-consent', '/book-demo'];
    if (blockedPrefix.some((p) => path.startsWith(p))) {
      return () => {};
    }

    // Respect submit + suppression windows in production behavior.
    // On localhost, bypass suppression so testing is predictable.
    if (!isLocalhost) {
      const now = Date.now();
      const suppressUntil = Math.max(
        parseInt(localStorage.getItem(POPUP_SUPPRESS_KEY) || '0', 10),
        parseInt(localStorage.getItem(LEGACY_DEMO_SUPPRESS_KEY) || '0', 10)
      );
      const alreadyStarted =
        localStorage.getItem(POPUP_CONVERTED_KEY) === '1' ||
        localStorage.getItem(LEGACY_DEMO_SUBMITTED_KEY) === '1';
      if (alreadyStarted || suppressUntil > now) {
        return () => {};
      }
    }

    const MIN_TIME_MS = 15000;
    const MIN_SCROLL_PERCENT = 30;
    const isEligible = (mode = 'fallback') => {
      const elapsedOk = Date.now() - pageEnterMsRef.current >= MIN_TIME_MS;
      const scrollPercent = ((window.scrollY + window.innerHeight) / Math.max(document.documentElement.scrollHeight, window.innerHeight)) * 100;
      const scrollOk = scrollPercent >= MIN_SCROLL_PERCENT;
      // Exit intent should not be blocked by scroll depth; fallback checks should.
      const modeOk = mode === 'exit' ? true : scrollOk;
      return elapsedOk && modeOk && !hasShownRef.current && !showTrialPopup;
    };

    const openPopup = (mode = 'fallback') => {
      if (!isEligible(mode)) return;
      hasShownRef.current = true;
      setShowTrialPopup(true);
    };

    // Local testing: show after 15s without exit intent.
    if (FORCE_TEST_POPUP_ON_LOCALHOST && isLocalhost) {
      mobileTimer = setTimeout(() => {
        if (hasShownRef.current || showTrialPopup) return;
        hasShownRef.current = true;
        setShowTrialPopup(true);
      }, MIN_TIME_MS);

      return () => {
        if (mobileTimer) clearTimeout(mobileTimer);
      };
    }

    // Hard fallback for production reliability:
    // show around 20s if user is still on page and popup not yet shown.
    hardFallbackTimer = setTimeout(() => {
      openPopup('exit');
    }, 20000);

    // Desktop: true exit intent (cursor leaves at top edge).
    const onMouseLeave = (e) => {
      if (window.innerWidth < 1024) return;
      if (e.clientY <= 0) openPopup('exit');
    };

    // Mobile: intent to leave approximation = engaged user scrolls sharply upward near top.
    const onScroll = () => {
      const y = window.scrollY || 0;
      const delta = y - lastScrollYRef.current;
      lastScrollYRef.current = y;

      if (window.innerWidth >= 1024) return;
      // Quick upward movement after meaningful scroll depth.
      const scrolledEnough = y > 260;
      const scrollingUpFast = delta < -80;
      const nowNearTop = y < 140;
      if (scrolledEnough && scrollingUpFast && nowNearTop) {
        openPopup();
      }
    };

    document.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('scroll', onScroll, { passive: true });

    // Desktop fallback: if user is engaged (15s + 30% scrolled), still show even
    // when mouseleave isn't detected in some browser/window setups.
    if (window.innerWidth >= 1024) {
      desktopFallbackInterval = setInterval(() => {
        openPopup();
      }, 3000);
    }

    // Mobile fallback: long dwell + engaged scroll.
    if (window.innerWidth < 1024) {
      mobileTimer = setTimeout(() => {
        openPopup();
      }, 45000);
    }

    return () => {
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);
      if (mobileTimer) clearTimeout(mobileTimer);
      if (desktopFallbackInterval) clearInterval(desktopFallbackInterval);
      if (hardFallbackTimer) clearTimeout(hardFallbackTimer);
    };
  }, [location.pathname]);

  // Freeze page scroll when popup open (better UX).
  useEffect(() => {
    if (!showTrialPopup) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showTrialPopup]);

  const closeTrialPopup = () => {
    localStorage.setItem(POPUP_SUPPRESS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setShowTrialPopup(false);
  };

  const onExitPopupDemoCtaClick = () => {
    localStorage.setItem(POPUP_CONVERTED_KEY, '1');
    setShowTrialPopup(false);
  };

  return (
    <>
      {/* Navigation */}
      <nav className="bg-white shadow-lg sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <DocumentCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  SmileConsent
                </h1>
              </Link>
            </div>
            
            {/* Navigation Menu - Hidden on mobile, shown on desktop */}
            <div className="hidden lg:flex items-center space-x-6">
              <div className="relative group">
                <button className="text-gray-600 hover:text-gray-900 font-medium transition-colors flex items-center space-x-1">
                  <span>Solutions</span>
                  <svg className="w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-2">
                    <Link to="/dental-consent-forms" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Dental Consent Forms
                    </Link>
                    <Link to="/dental-consent-plans" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Consent Plans
                    </Link>
                    <Link to="/insights/consent-is-coming" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Insights: Consent & Regulation
                    </Link>
                    <Link to="/nhs-dental-contract-calculators" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-teal-600">
                      NHS contract calculators
                    </Link>
                    <Link to="/dental-compliance-software" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Compliance Software
                    </Link>
                    <Link to="/digital-consent-dentists" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Digital Consent
                    </Link>
                    <Link to="/dental-consent-software" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Consent Software
                    </Link>
                    <Link to="/dental-google-reviews" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-amber-600">
                      Google Reviews
                    </Link>
                    <div className="border-t border-gray-200 my-1"></div>
                    <Link to="/dental-practice-management" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Practice Management
                    </Link>
                    <Link to="/dental-templates" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Dental Templates
                    </Link>
                    <div className="border-t border-gray-200 my-1"></div>
                    <Link to="/dentaltriage" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      🚨 Dental Emergency Triage
                    </Link>
                    <Link to="/cpd-legal-consent" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600">
                      Free CPD course (1hr)
                    </Link>
                    {onCapacityTest && (
                      <button
                        type="button"
                        onClick={onCapacityTest}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-teal-700"
                      >
                        Mental capacity assessment
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">
                Contact
              </Link>
            </div>
            
            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="text-gray-600 hover:text-gray-900 p-2"
                aria-label="Toggle mobile menu"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showMobileMenu ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>

            {/* Navigation Buttons - Hidden on mobile (trial + demo live in hero; Capacity Test is under Solutions) */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link to="/login" className="text-gray-700 hover:text-gray-900 font-medium transition-colors px-4 py-2 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50">
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {showMobileMenu && (
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-lg">
          <div className="px-4 py-2 space-y-1">
            {/* Solutions Dropdown */}
            <div className="py-2">
              <div className="text-sm font-medium text-gray-500 px-3 py-2">Solutions</div>
              <Link 
                to="/dental-consent-forms" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Dental Consent Forms
              </Link>
              <Link 
                to="/dental-consent-plans" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Consent Plans
              </Link>
              <Link 
                to="/insights/consent-is-coming" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Insights: Consent & Regulation
              </Link>
              <Link 
                to="/nhs-dental-contract-calculators" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-teal-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                NHS contract calculators
              </Link>
              <Link 
                to="/dental-compliance-software" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Compliance Software
              </Link>
              <Link 
                to="/digital-consent-dentists" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Digital Consent
              </Link>
              <Link 
                to="/dental-consent-software" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Consent Software
              </Link>
              <Link 
                to="/dental-google-reviews" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-amber-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Google Reviews
              </Link>
              <Link 
                to="/dental-practice-management" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Practice Management
              </Link>
              <Link 
                to="/dental-templates" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Dental Templates
              </Link>
              <Link 
                to="/dentaltriage" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md font-medium"
                onClick={() => setShowMobileMenu(false)}
              >
                🚨 Dental Emergency Triage
              </Link>
              <Link 
                to="/cpd-legal-consent" 
                className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
                onClick={() => setShowMobileMenu(false)}
              >
                Free CPD course (1hr)
              </Link>
              {onCapacityTest && (
                <button
                  type="button"
                  onClick={() => {
                    onCapacityTest();
                    setShowMobileMenu(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-teal-700 rounded-md"
                >
                  Mental capacity assessment
                </button>
              )}
            </div>
            
            <div className="border-t border-gray-200 my-2"></div>
            
            {/* Other Links */}
            <Link 
              to="/contact" 
              className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md"
              onClick={() => setShowMobileMenu(false)}
            >
              Contact
            </Link>
            
            {/* Mobile Action Buttons */}
            <div className="pt-4 space-y-3">
              <Link 
                to="/login" 
                className="block w-full text-center text-gray-700 hover:text-gray-900 font-medium transition-colors px-4 py-3 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                onClick={() => setShowMobileMenu(false)}
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="block w-full text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                onClick={() => setShowMobileMenu(false)}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Exit-intent: book a free demo CTA */}
      {showTrialPopup && (
        <div className="fixed inset-0 z-[110] flex items-end justify-center sm:items-center p-3 sm:p-5 md:p-8">
          <button
            type="button"
            onClick={closeTrialPopup}
            className="absolute inset-0 z-0 bg-gray-900/55 backdrop-blur-[1px]"
            aria-label="Close demo offer overlay"
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="trial-popup-title"
            className="relative z-10 flex max-h-[min(92dvh,880px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-2xl shadow-indigo-500/15 sm:max-w-3xl md:max-w-4xl lg:max-w-5xl sm:rounded-3xl"
          >
            {/* Wide top banner */}
            <div className="relative shrink-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 px-4 py-4 sm:px-8 sm:py-5 md:px-10 md:py-6">
              <button
                type="button"
                onClick={closeTrialPopup}
                className="absolute right-2 top-2 sm:right-4 sm:top-1/2 sm:-translate-y-1/2 inline-flex touch-manipulation items-center justify-center rounded-full border border-white/30 bg-white/15 text-white min-h-[44px] min-w-[44px] hover:bg-white/25 active:bg-white/35"
                aria-label="Close demo offer"
              >
                <XMarkIcon className="h-6 w-6" strokeWidth={2} />
              </button>
              <p className="inline-flex max-w-full items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white ring-1 ring-white/25 sm:text-xs">
                <SparklesIcon className="h-4 w-4 shrink-0" />
                Free, no obligation
              </p>
              <h3
                id="demo-popup-title"
                className="mt-3 pr-12 text-xl font-black uppercase leading-snug tracking-tight text-white sm:mt-4 sm:pr-14 sm:text-2xl md:text-3xl lg:text-4xl"
              >
                Book a free demo
              </h3>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-indigo-100 sm:text-base">
                See Smile Consent live — AI consent generation, digital signing, and UK-ready workflows. Pick a time that suits your team.
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-5 sm:px-8 sm:pt-6 md:px-10 md:pt-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-10">
                <ul className="flex-1 space-y-3 text-sm text-gray-700 sm:text-base">
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-emerald-600 font-bold" aria-hidden>
                      ✓
                    </span>
                    <span>
                      <strong className="text-gray-900">Live walkthrough</strong> — tailored to your practice and questions
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-emerald-600 font-bold" aria-hidden>
                      ✓
                    </span>
                    <span>
                      Built for <strong className="text-gray-900">UK dental teams</strong> — consent workflows that fit how you work
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-0.5 shrink-0 text-emerald-600 font-bold" aria-hidden>
                      ✓
                    </span>
                    <span>
                      <strong className="text-gray-900">No obligation</strong> — book a slot and see if Smile Consent is right for you
                    </span>
                  </li>
                </ul>

                <div className="w-full shrink-0 lg:max-w-sm lg:pt-1">
                  <Link
                    to="/book-demo"
                    onClick={onExitPopupDemoCtaClick}
                    className="flex min-h-[52px] w-full items-center justify-center rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-indigo-700 px-4 py-3.5 text-center text-base font-extrabold text-white shadow-lg shadow-indigo-600/30 transition hover:from-blue-700 hover:via-indigo-700 hover:to-indigo-800 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 sm:py-4 sm:text-lg"
                  >
                    Book my free demo
                  </Link>
                  <button
                    type="button"
                    onClick={closeTrialPopup}
                    className="mt-3 w-full min-h-[48px] rounded-xl border border-gray-200 px-4 py-3 text-base font-medium text-gray-600 hover:bg-gray-50 active:bg-gray-100"
                  >
                    No thanks
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
